import React from 'react'
import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { API_BASE_URL } from './config';

// Hooks and context
import { useAppContext } from './hooks/useAppContext';
import useAppStore from './store/useAppStore';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useWebPush } from './hooks/useWebPush';

// Layout
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';

import { ConfirmationModal } from './components/common/ConfirmationModal';
import { Skeleton } from './components/common/Skeleton';
import { IntroAnimation } from './components/common/IntroAnimation';
import ModernLoginScreen from './screens/ModernLoginScreen';
import { VersionCheck } from './components/common/VersionCheck';
import WorkflowRequestModal from './screens/employee/WorkflowRequestModal';

// Screens
import LoginScreen from './screens/LoginScreen';
import OnboardingScreen from './screens/common/OnboardingScreen';

// Dashboards
import AdminDashboardScreen from './screens/admin/AdminDashboardScreen';
import EmployeeDashboardScreen from './screens/employee/EmployeeDashboardScreen';

// Employee Screens
import SalesReportsScreen from './screens/employee/SalesReportsScreen';
import MaintenanceReportsScreen from './screens/employee/MaintenanceReportsScreen';
import ProjectReportsScreen from './screens/employee/ProjectReportsScreen';
import ProjectDashboardScreen from './screens/employee/ProjectDashboardScreen';
// Accounting Screens (removed: purchase archive & expenses)
import CreateQuotationScreen from './screens/employee/CreateQuotationScreen';
import QuotationDetailScreen from './screens/employee/QuotationDetailScreen';
import TeamProjectsScreen from './screens/employee/TeamProjectsScreen';
import ReportsLogScreen from './screens/employee/ReportsLogScreen';
import WorkflowScreen from './screens/employee/WorkflowScreen';
import WorkflowDetailScreen from './screens/employee/WorkflowDetailScreen';
import ProfileScreen from './screens/employee/ProfileScreen';
import EmployeeAnalyticsScreen from './screens/employee/EmployeeAnalyticsScreen';
import PackageRequestsScreen from './screens/employee/PackageRequestsScreen';
import PackageDetailScreen from './screens/employee/PackageDetailScreen';
import CalendarScreen from './screens/employee/CalendarScreen';

// Admin Screens
import AdminQuotationsScreen from './screens/admin/AdminQuotationsScreen';
import ManageEmployeesScreen from './screens/admin/ManageEmployeesScreen';
import AdminEmployeeDetailScreen from './screens/admin/AdminEmployeeDetailScreen';
import ManageBranchesScreen from './screens/admin/ManageBranchesScreen';
import ManageTeamsScreen from './screens/admin/ManageTeamsScreen';
import ServiceEvaluationScreen from './screens/admin/ServiceEvaluationScreen';
import AdminAnalyticsScreen from './screens/admin/AnalyticsScreen';

import ManagePermissionsScreen from './screens/admin/ManagePermissionsScreen';
import ComponentsShowcaseScreen from './screens/admin/ComponentsShowcaseScreen';
import AdminProjectReportsScreen from './screens/admin/AdminProjectReportsScreen';
import AdminPackageManagementScreen from './screens/admin/AdminPackageManagementScreen';

// Common Screens
import ReportDetailScreen from './screens/common/ReportDetailScreen';
import TechnicalSupportScreen from './screens/common/TechnicalSupportScreen';
import NaseehScreen from './screens/common/NaseehScreen';
import QSReportScreen from './screens/common/QSReportScreen';

import { Role, Report, WorkflowRequest, ReportType, User } from './types';
import { PrintableView } from './components/ui/PrintableView';
import { createPortal } from 'react-dom';
import AccessGuard from './components/common/AccessGuard';

// Wrapper components to fetch data for dynamic routes
const ReportDetailWrapper = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { reports, addReport } = useAppStore();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const reportInStore = reports.find(r => r.id === reportId) || null;
  const [report, setReport] = React.useState<Report | null>(reportInStore);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchReport = async () => {
      if (reportInStore || !reportId || !user) return;
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_BASE_URL}/reports/${reportId}`, {
          headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
        });
        if (!res.ok) throw new Error(`خطأ عند جلب التقرير: ${res.status}`);
        const data = await res.json();
        if (data && data.id) {
          setReport(data as Report);
          addReport(data as Report);
        } else {
          setError('لم يتم العثور على التقرير');
        }
      } catch (e: any) {
        setError(e?.message || 'تعذر جلب التقرير');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId, reportInStore, addReport, user]);

  if (report) return <ReportDetailScreen report={report} />;

  if (loading || !user) {
    return (
      <div className="p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-4 w-96 mb-2" />
        <Skeleton className="h-4 w-80" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error}</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-3 py-2 rounded bg-slate-200">رجوع</button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <p>لم يتم العثور على التقرير.</p>
      <button onClick={() => navigate('/')} className="mt-4 px-3 py-2 rounded bg-slate-200">العودة للرئيسية</button>
    </div>
  );
};

const WorkflowDetailWrapper = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { requests } = useAppStore();
  const request = requests.find(r => r.id === workflowId);
  return request ? <WorkflowDetailScreen request={request} /> : <Navigate to="/workflow" replace />;
};

const EditSalesReportWrapper = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { reports } = useAppStore();
  const report = reports.find(r => r.id === reportId && r.type === ReportType.Sales) || null;
  return <SalesReportsScreen reportToEdit={report} />;
};

const EditMaintenanceReportWrapper = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { reports } = useAppStore();
  const report = reports.find(r => r.id === reportId && r.type === ReportType.Maintenance) || null;
  return <MaintenanceReportsScreen reportToEdit={report} />;
};

const EditProjectReportWrapper = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { reports } = useAppStore();
  const report = reports.find(r => r.id === reportId && r.type === ReportType.Project) || null;
  return <ProjectReportsScreen reportToEdit={report} />;
};

const PrintPortalBridge: React.FC = () => {
  const { reportForPrinting, clearReportForPrinting } = useAppStore();

  React.useEffect(() => {
    if (!reportForPrinting) return;
    const handleAfterPrint = () => {
      clearReportForPrinting();
      window.removeEventListener('afterprint', handleAfterPrint);
    };
    window.addEventListener('afterprint', handleAfterPrint);
    const printTimeout = setTimeout(() => window.print(), 100);
    // Fallback clear in case afterprint doesn't fire
    const fallbackTimeout = setTimeout(() => clearReportForPrinting(), 6000);
    return () => {
      clearTimeout(printTimeout);
      clearTimeout(fallbackTimeout);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [reportForPrinting, clearReportForPrinting]);

  if (!reportForPrinting) return null;
  const mountEl = document.getElementById('print-mount-point');
  if (!mountEl) return null;
  return createPortal(<PrintableView report={reportForPrinting} />, mountEl);
};

import { ModernSidebar } from './components/layout/ModernSidebar';
import { ModernHeader } from './components/layout/ModernHeader';

const AppLayout: React.FC = () => {
  const { isSidebarCollapsed, isMobileMenuOpen, setMobileMenuOpen } = useAppStore();
  const location = useLocation();
  const [pageReady, setPageReady] = useState(false);
  const { user, designMode, theme } = useAppContext();

  useEffect(() => {
    // Close mobile menu on route change
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  useEffect(() => {
    setPageReady(false);
    const t = setTimeout(() => setPageReady(true), 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const isModern = designMode === 'modern';

  return (
    <div className={`min-h-screen ${isModern ? 'bg-slate-900 text-slate-100' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200'}`} dir="rtl">
      {isModern ? <ModernSidebar /> : <Sidebar />}

      <div
        className={`flex flex-col min-h-screen transition-all duration-300 
          ${isMobileMenuOpen ? '' : ''} 
          ${isSidebarCollapsed ? (isModern ? 'lg:ps-24' : 'lg:ps-20') : (isModern ? 'lg:ps-80' : 'lg:ps-64')}
        `}
      >
        {isModern ? (
          <ModernHeader toggleMobileMenu={() => setMobileMenuOpen(!isMobileMenuOpen)} />
        ) : (
          <Header toggleMobileMenu={() => setMobileMenuOpen(!isMobileMenuOpen)} />
        )}

        {/* Mobile Overlay to prevent content interaction when menu is open */}
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[55] lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
        <main className={`flex-1 overflow-y-auto p-4 md:p-6 transition-opacity transition-transform duration-500 ease-out ${pageReady ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-95'} ${isMobileMenuOpen ? 'pointer-events-none' : 'pointer-events-auto'}`}>
          <Routes>
            {user?.role === Role.Admin ? (
              <>
                <Route path="/" element={<AdminDashboardScreen />} />
                <Route path="/employees" element={<ManageEmployeesScreen />} />
                <Route path="/employees/:employeeId" element={<AdminEmployeeDetailScreen />} />
                <Route path="/branches" element={<ManageBranchesScreen />} />
                <Route path="/teams" element={<ManageTeamsScreen />} />
                <Route path="/evaluation" element={<ServiceEvaluationScreen />} />
                <Route path="/analytics" element={<AdminAnalyticsScreen />} />

                <Route path="/permissions" element={<ManagePermissionsScreen />} />
                <Route path="/showcase" element={<ComponentsShowcaseScreen />} />
                {/* Alias: ensure /packages works for Admins by redirecting to /admin-packages */}
                <Route path="/packages" element={<Navigate to="/admin-packages" replace />} />
                <Route path="/admin-projects" element={<AdminProjectReportsScreen />} />
                <Route path="/admin-packages" element={<AccessGuard requiresPackageManagement><AdminPackageManagementScreen /></AccessGuard>} />
                <Route path="/admin-packages/:id" element={<AccessGuard requiresPackageManagement><PackageDetailScreen /></AccessGuard>} />
                <Route path="/admin-quotations" element={<AdminQuotationsScreen />} />
                <Route path="/quotations/:id" element={<QuotationDetailScreen />} />
                <Route path="/calendar" element={<CalendarScreen />} />
              </>
            ) : (
              <>
                <Route path="/" element={<EmployeeDashboardScreen />} />
                <Route path="/sales" element={<AccessGuard requiredType={ReportType.Sales}><SalesReportsScreen /></AccessGuard>} />
                <Route path="/sales/edit/:reportId" element={<AccessGuard requiredType={ReportType.Sales}><EditSalesReportWrapper /></AccessGuard>} />
                <Route path="/maintenance" element={<AccessGuard requiredType={ReportType.Maintenance}><MaintenanceReportsScreen /></AccessGuard>} />
                <Route path="/maintenance/edit/:reportId" element={<AccessGuard requiredType={ReportType.Maintenance}><EditMaintenanceReportWrapper /></AccessGuard>} />
                <Route path="/projects" element={<AccessGuard requiredType={ReportType.Project}><ProjectReportsScreen /></AccessGuard>} />
                <Route path="/projects/new" element={<AccessGuard requiredType={ReportType.Project}><ProjectReportsScreen /></AccessGuard>} />
                <Route path="/projects/edit/:reportId" element={<AccessGuard requiredType={ReportType.Project}><EditProjectReportWrapper /></AccessGuard>} />
                <Route path="/project-dashboard" element={<ProjectDashboardScreen />} />
                <Route path="/quotations/new" element={<CreateQuotationScreen />} />
                <Route path="/quotations/:id" element={<QuotationDetailScreen />} />
                <Route path="/team-projects" element={<AccessGuard requiresTeamLead><TeamProjectsScreen /></AccessGuard>} />
                <Route path="/log" element={<ReportsLogScreen />} />
                <Route path="/analytics" element={<EmployeeAnalyticsScreen />} />
                <Route path="/packages" element={<AccessGuard requiresPackageManagement><PackageRequestsScreen /></AccessGuard>} />
                <Route path="/packages/:id" element={<AccessGuard requiresPackageManagement><PackageDetailScreen /></AccessGuard>} />
                <Route path="/calendar" element={<CalendarScreen />} />
              </>
            )}
            {/* Common Routes */}
            {/* Removed: purchase archive and expenses routes */}
            <Route path="/workflow" element={<AccessGuard requiresImportExport><WorkflowScreen /></AccessGuard>} />
            <Route path="/workflow/:workflowId" element={<AccessGuard requiresImportExport><WorkflowDetailWrapper /></AccessGuard>} />
            <Route path="/reports/:reportId" element={<ReportDetailWrapper />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/support" element={<TechnicalSupportScreen />} />
            {/* QSREPORT print template route */}
            <Route path="/qsreport" element={<QSReportScreen />} />
            <Route path="/naseeh" element={<NaseehScreen />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      {/* <NaseehWidget /> */}
      <WorkflowRequestModal isOpen={useAppStore(s => s.isWorkflowModalOpen)} />
      <PrintPortalBridge />
    </div>
  );
};

const App: React.FC = () => {
  const { user, isLoading, theme, updateUser, designMode } = useAppContext();
  const navigate = useNavigate();
  const { isDataLoading, confirmationState, closeConfirmation, fetchInitialData, users } = useAppStore();
  const [showIntro, setShowIntro] = useState(false);

  // Initialize Push Notifications with user id
  usePushNotifications(user?.id);
  // Initialize Web Push for PWA on supported browsers
  useWebPush(user?.id);

  // Check if intro has been shown in this session
  useEffect(() => {
    const hasShownIntro = sessionStorage.getItem('hasShownIntro');
    if (!hasShownIntro) {
      setShowIntro(true);
    }
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
    sessionStorage.setItem('hasShownIntro', 'true');
  };

  // Trigger initial data fetch once user is available
  useEffect(() => {
    if (user) {
      fetchInitialData(user);
    }
  }, [user, fetchInitialData]);

  // Sync logged-in user's permissions with latest server data
  useEffect(() => {
    if (!user || !Array.isArray(users) || users.length === 0) return;
    const latest: User | undefined = users.find(u => u.id === user.id);
    if (!latest) return;
    // If permissions or role changed, update AppContext user
    // Normalize booleans from either camelCase or snake_case without short-circuiting on false
    const hasPkg = !!(((latest as any).hasPackageManagementPermission) ?? ((latest as any).has_package_management_permission));
    const hasImpExp = !!(((latest as any).hasImportExportPermission) ?? ((latest as any).has_import_export_permission));
    const hasPurch = !!((((latest as any).hasPurchaseManagementPermission) ?? ((latest as any).has_purchase_management_permission)));
    const roleChanged = String(latest.role) !== String(user.role);
    const pkgChanged = hasPkg !== !!user.hasPackageManagementPermission;
    const impExpChanged = hasImpExp !== !!user.hasImportExportPermission;
    const purchChanged = hasPurch !== !!(user as any).hasPurchaseManagementPermission;
    if (roleChanged || pkgChanged || impExpChanged || purchChanged) {
      updateUser({
        role: latest.role as any,
        hasPackageManagementPermission: hasPkg,
        hasImportExportPermission: hasImpExp,
        hasPurchaseManagementPermission: hasPurch,
      });
    }
  }, [users, user, updateUser]);

  // Apply theme class to <html> element
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    // Also add design mode data attribute
    root.setAttribute('data-design', designMode);
  }, [theme, designMode]);

  useEffect(() => {
    if (!isLoading) {
      if (!user) navigate('/login');
    }
  }, [user, isLoading, navigate]);

  return (
    <>
      {showIntro && <IntroAnimation onComplete={handleIntroComplete} />}
      <VersionCheck />
      <Toaster position="top-left" reverseOrder={false} />
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        message={confirmationState.message || ''}
        onConfirm={confirmationState.onConfirm}
        onCancel={closeConfirmation}
      />
      <Routes>
        {!user ? (
          <>
            <Route path="/login" element={designMode === 'modern' ? <ModernLoginScreen /> : <LoginScreen />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : isDataLoading ? (
          <Route path="*" element={
            <div className="flex h-screen items-center justify-center bg-slate-100 dark:bg-slate-800">
              <p>جاري تحميل البيانات...</p>
            </div>
          } />
        ) : (
          <Route path="/*" element={<AppLayout />} />
        )}
      </Routes>
    </>
  );
};

export default App;
